import { useLoading } from "../context/LoadingContext";

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaSearch } from "react-icons/fa";
import SelectBoxFornecedor from "../components/SelectBoxFornecedor";
import { useRestaurante } from "../context/RestauranteContext";
import { useUser } from "../context/UserContext";
import "../css/RelatorioAproveitamento.css"; // Reutilizando o CSS para consistência
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import BackButton from "../components/BackButton";

registerLocale("pt-BR", ptBR);

const RelatorioNotas = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { restauranteId, setRestauranteId } = useRestaurante();
  const { user } = useUser();
  const [fornecedorId, setFornecedorId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [reportData, setReportData] = useState([]);
  const [uniqueIngredients, setUniqueIngredients] = useState([]);
  const [biAnalysis, setBiAnalysis] = useState(null);
  const { isLoading, setIsLoading } = useLoading();

  const firstDayOfMonth = React.useMemo(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1), []);
  const lastDayOfMonth = React.useMemo(() => new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0), []);

  const [dataInicio, setDataInicio] = useState(firstDayOfMonth);
  const [dataFim, setDataFim] = useState(lastDayOfMonth);

  useEffect(() => {
    if (id) {
      setRestauranteId(id);
      if (restauranteId && user?.access_token) {
        handleSearch();
      }
    }
  }, [id, setRestauranteId]);

  const handleSearch = async () => {
    const toastId = toast.loading("Buscando relatório...");
    setIsLoading(true);
    const token = user?.access_token;
    if (!token) {
      console.error("Token de autenticação não encontrado.");
      toast.update(toastId, {
        render: "Token de autenticação não encontrado.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      setIsLoading(false);
      return;
    }

    const inicio = dataInicio ? dataInicio : firstDayOfMonth;
    const fim = dataFim ? dataFim : lastDayOfMonth;

    const formattedDataInicio = inicio.toISOString().split("T")[0];
    const formattedDataFim = fim.toISOString().split("T")[0];

    if (restauranteId && token) {
      try {
        let url = `${process.env.REACT_APP_BASE_URL}/api/v1/relatorios/notas?idRestaurante=${restauranteId}`;
        url += `&dataInicio=${formattedDataInicio}`;
        url += `&dataFim=${formattedDataFim}`;

        if (searchTerm) url += `&nomeIngrediente=${searchTerm}`;
        if (fornecedorId) url += `&fornecedorId=${fornecedorId}`;

        const response = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authentication: token,
          },
        });
        const result = await response.json();
        processNotesData(result.notas, result.resumo);
        toast.update(toastId, {
          render: "Relatório carregado com sucesso!",
          type: "success",
          isLoading: false,
          autoClose: 2000,
        });
      } catch (error) {
        console.error("Erro ao buscar notas:", error);
        setReportData([]);
        setBiAnalysis(null);
        toast.update(toastId, {
          render: "Erro ao buscar notas.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
    setIsLoading(false);
  };

  const handleBack = () => {
    navigate(`/restaurantes/${id}/relatorios`);
  };

  const processNotesData = useCallback((notas, resumo) => {
    if (!notas || notas.length === 0) {
      setReportData([]);
      setBiAnalysis(null);
      return;
    }

    // Análise de BI
    setBiAnalysis(resumo);

    // Dados para o Gráfico (Gasto por Data e Ingrediente)
    const dataMap = new Map();
    const uniqueIngredientsSet = new Set();

    notas.forEach((item) => {
      const dataCompra = item.dataCompra.split("T")[0]; // Assuming dataCompra is in ISO format
      if (!dataMap.has(dataCompra)) {
        dataMap.set(dataCompra, { dataCompra });
      }
      dataMap.get(dataCompra)[item.nomeIngrediente] =
        (dataMap.get(dataCompra)[item.nomeIngrediente] || 0) + item.preco;
      uniqueIngredientsSet.add(item.nomeIngrediente);
    });

    const sortedReportData = Array.from(dataMap.values()).sort(
      (a, b) => new Date(a.dataCompra) - new Date(b.dataCompra)
    );

    // Fill in missing ingredient data with 0 for each date
    const finalReportData = sortedReportData.map((dataPoint) => {
      const newDataPoint = { ...dataPoint };
      uniqueIngredientsSet.forEach((ingredient) => {
        if (newDataPoint[ingredient] === undefined) {
          newDataPoint[ingredient] = 0;
        }
      });
      return newDataPoint;
    });

    setReportData(finalReportData);
    setUniqueIngredients(Array.from(uniqueIngredientsSet));
  }, [setReportData, setBiAnalysis, setUniqueIngredients]);

    return (
    <div className="relatorio-container">
      <ToastContainer />
      <BackButton inline />
      <h2>Relatório de Notas</h2>

      <div className="filtro-container-aproveitamento">
        <div className="filtro-group">
          <label>Nome do Ingrediente:</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por ingrediente"
            className="text-input"
          />
        </div>
        <div className="filtro-group">
          <label>Data Início:</label>
          <DatePicker
            selected={dataInicio}
            onChange={(date) => setDataInicio(date)}
            dateFormat="dd/MM/yyyy"
            locale="pt-BR"
            placeholderText="Selecione a data de início"
            className="date-picker"
          />
        </div>
        <div className="filtro-group">
          <label>Data Fim:</label>
          <DatePicker
            selected={dataFim}
            onChange={(date) => setDataFim(date)}
            dateFormat="dd/MM/yyyy"
            locale="pt-BR"
            placeholderText="Selecione a data de fim"
            className="date-picker"
          />
        </div>
        <div className="filtro-group">
          <label>Fornecedor:</label>
          <SelectBoxFornecedor
            value={fornecedorId}
            onChange={setFornecedorId}
          />
        </div>
        <button
          onClick={handleSearch}
          className={isLoading ? "search-button-loading" : "search-button"}
          disabled={isLoading}
        >
          <FaSearch /> {isLoading ? "Buscando..." : "Buscar"}
        </button>
      </div>

      <div className="relatorio-grafico-container">
        <h3>Análise de Gastos e Desperdícios</h3>
        {isLoading ? (
          <div className="loading-message">
            Carregando dados do relatório...
          </div>
        ) : reportData.length > 0 && biAnalysis ? (
          <>
            <div className="bi-metrics">
              <p>
                <strong>Valor Total:</strong>{" "}
                {biAnalysis.valorTotal?.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </p>
              <p>
                <strong>Ingrediente Mais Caro:</strong>{" "}
                {biAnalysis?.itemMaisCaro}
              </p>
              <p>
                <strong>Fornecedor com Maior Gasto:</strong>{" "}
                {biAnalysis.fornecedorMaiorGasto}
              </p>
              <p>
                <strong>Item Mais Comprado:</strong>{" "}
                {biAnalysis.itemMaisComprado}
              </p>
              <p>
                <strong>Total de Desperdício (Peso/Und):</strong>{" "}
                {biAnalysis.totalDescartado?.toFixed(2)}
              </p>
              <p>
                <strong>Ingrediente com Maior Descarte:</strong>{" "}
                {biAnalysis.ingredienteMaiorDescarte}
              </p>
              <p>
                <strong>Valor Médio por Nota:</strong>{" "}
                {biAnalysis.valorMedioPorNota}
              </p>
            </div>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                data={reportData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="dataCompra" />
                <YAxis />
                <Tooltip
                  formatter={(value) =>
                    value.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })
                  }
                />
                <Legend />
                {uniqueIngredients.map((ingredient, index) => (
                  <Line
                    key={ingredient}
                    type="monotone"
                    dataKey={ingredient}
                    stroke={`hsl(${index * 137}, 70%, 50%)`} // Generate a distinct color for each line
                    name={ingredient}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </>
        ) : (
          <p className="no-data-message">
            Nenhum dado encontrado para os filtros selecionados.
          </p>
        )}
      </div>
    </div>
  );
};

export default RelatorioNotas;
